import { useEffect, useRef, useState, type FormEvent } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@coval/ui"

interface RenameNotebookDialogProps {
  open: boolean
  title: string
  onClose: () => void
  onRename: (title: string) => Promise<void>
}

/** Standard rename dialog: one field, Enter or Save to commit. */
export function RenameNotebookDialog({
  open,
  title,
  onClose,
  onRename,
}: RenameNotebookDialogProps) {
  const [draft, setDraft] = useState(title)
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  // Opening resets the draft. A title saved elsewhere while open replaces the draft only if the user hasn't edited it, so a typed name, a sending rename or a failed retry is never overwritten.
  const lastOpen = useRef(false)
  const lastTitle = useRef(title)
  useEffect(() => {
    const opened = open && !lastOpen.current
    const retitled =
      open && title !== lastTitle.current && draft === lastTitle.current
    if (opened || retitled) {
      setDraft(title)
      setFailure(null)
    }
    lastOpen.current = open
    lastTitle.current = title
  }, [draft, open, title])

  // The rename keeps running once sent, so the dialog stays open until it settles and can show a failure.
  const close = () => {
    if (!saving) {
      onClose()
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const next = draft.trim()
    if (!next || saving) {
      return
    }
    if (next === title) {
      onClose()
      return
    }
    setSaving(true)
    setFailure(null)
    try {
      await onRename(next)
      onClose()
    } catch (renameError: unknown) {
      // Keep the dialog open so the name can be retried.
      setFailure(
        renameError instanceof Error
          ? renameError.message
          : "Could not rename the notebook.",
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent>
        <form onSubmit={(event) => void submit(event)}>
          <DialogHeader>
            <DialogTitle>Rename Notebook</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Input
              aria-label="Notebook name"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Notebook name"
              maxLength={200}
            />
            {failure ? (
              <p role="alert" className="mt-2 text-sm text-coval-danger-text">
                {failure}
              </p>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={close}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!draft.trim() || saving}
              aria-busy={saving}
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
